"use client";

import { c as _c } from "react/compiler-runtime";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
const DIVIDER_ID = "__divider__";
export interface SortablePickerDivider {
  /** Index in `selected` where the fallback tier begins. 0..selected.length. */
  index: number;
  /** Label shown on the divider row. */
  label?: string;
}
export interface SortablePickerProps {
  label: string;
  options: string[];
  /** Active items in priority order (index 0 = highest priority). */
  selected: string[];
  /** Called when the active list changes. When a divider is configured,
   *  the second arg carries the new divider index — emitted together with
   *  the new array so callers can persist both atomically (no DB races). */
  onChange: (next: string[], dividerIndex?: number) => void;
  /** Override column headers if needed. */
  availableLabel?: string;
  activeLabel?: string;
  /** Optional tier divider — when set, splits the active list into a
   *  primary section (above) and a fallback section (below). */
  divider?: SortablePickerDivider;
}

// Class names map to semantic rules in globals.css (.sp-*).
const groupLabel = "sp-group-label";
const twoCol = "sp-two-col";
const column = "sp-column";
const columnLabel = "sp-column-label";
const columnBody = "sp-column-body";
const inactiveChip = "sp-inactive-chip";
const emptyState = "sp-empty";
const activeRow = "sp-row sp-row-active";
const fallbackRow = "sp-row sp-row-fallback";
const dragHandle = "sp-drag-handle";
const rankPill = "sp-rank-pill";
const rankPillFallback = "sp-rank-pill sp-rank-pill-fallback";
const rowLabel = "sp-row-label";
const rowActions = "sp-row-actions";
const iconBtn = "sp-icon-btn";
const dividerRow = "sp-divider-row";
const dividerLabel = "sp-divider-label";
export function SortablePicker(t0) {
  const $ = _c(20);
  const {
    label,
    options,
    selected,
    onChange,
    availableLabel: t1,
    activeLabel: t2,
    divider
  } = t0;
  const availableLabel = t1 === undefined ? "Verf\xFCgbar" : t1;
  const activeLabel = t2 === undefined ? "Aktiv (Prio \u2193)" : t2;
  let t3;
  if ($[0] !== selected) {
    t3 = o => !selected.includes(o);
    $[0] = selected;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const inactive = options.filter(t3);
  const dividerIdx = divider ? clamp(divider.index, 0, selected.length) : -1;
  const displayItems = divider ? [...selected.slice(0, dividerIdx), DIVIDER_ID, ...selected.slice(dividerIdx)] : selected;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      activationConstraint: {
        distance: 4
      }
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      coordinateGetter: sortableKeyboardCoordinates
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const sensors = useSensors(useSensor(PointerSensor, t4), useSensor(KeyboardSensor, t5));
  let t6;
  if ($[4] !== divider || $[5] !== onChange) {
    t6 = function applyDisplay(next) {
      if (divider) {
        const newDividerIdx = next.indexOf(DIVIDER_ID);
        const filtered = next.filter(_temp);
        onChange(filtered, newDividerIdx);
      } else {
        onChange(next);
      }
    };
    $[4] = divider;
    $[5] = onChange;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const applyDisplay = t6;
  const handleDragEnd = function handleDragEnd(e) {
    const {
      active,
      over
    } = e;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = displayItems.indexOf(String(active.id));
    const newIndex = displayItems.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    applyDisplay(arrayMove(displayItems, oldIndex, newIndex));
  };
  const add = function add(item) {
    if (divider) {
      const next_0 = [...selected.slice(0, dividerIdx), item, ...selected.slice(dividerIdx)];
      onChange(next_0, dividerIdx + 1);
    } else {
      onChange([...selected, item]);
    }
  };
  const remove = function remove(item_0) {
    const idx = selected.indexOf(item_0);
    if (idx === -1) {
      return;
    }
    const next_1 = selected.filter(s => s !== item_0);
    if (divider) {
      const newDividerIdx_0 = idx < dividerIdx ? dividerIdx - 1 : dividerIdx;
      onChange(next_1, newDividerIdx_0);
    } else {
      onChange(next_1);
    }
  };
  const move = function move(itemId, dir) {
    const idx_0 = displayItems.indexOf(itemId);
    if (idx_0 === -1) {
      return;
    }
    const newIdx = idx_0 + dir;
    if (newIdx < 0 || newIdx >= displayItems.length) {
      return;
    }
    applyDisplay(arrayMove(displayItems, idx_0, newIdx));
  };
  let t7;
  if ($[7] !== label) {
    t7 = <div className={groupLabel}>{label}</div>;
    $[7] = label;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== availableLabel) {
    t8 = <div className={columnLabel}>{availableLabel}</div>;
    $[9] = availableLabel;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = column;
  let t10;
  if ($[11] !== activeLabel) {
    t10 = <div className={columnLabel}>{activeLabel}</div>;
    $[11] = activeLabel;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const t11 = columnBody;
  const t12 = selected.length === 0 && !divider ? <div className={emptyState}>—</div> : <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}><SortableContext items={displayItems} strategy={verticalListSortingStrategy}>{displayItems.map((item_2, idx_1) => {
        if (item_2 === DIVIDER_ID) {
          return <DividerSortable key={DIVIDER_ID} label={divider?.label ?? "Fallback \u2193"} />;
        }
        const isFallback = divider !== undefined && idx_1 > dividerIdx;
        const tierRank = isFallback ? idx_1 - dividerIdx : idx_1 + 1;
        return <SortableRow key={item_2} id={item_2} rank={tierRank} fallback={isFallback} canMoveUp={idx_1 > 0} canMoveDown={idx_1 < displayItems.length - 1} onUp={() => move(item_2, -1)} onDown={() => move(item_2, 1)} onRemove={() => remove(item_2)} />;
      })}</SortableContext></DndContext>;
  let t13;
  if ($[13] !== t11 || $[14] !== t12) {
    t13 = <div className={t11}>{t12}</div>;
    $[13] = t11;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t10 || $[17] !== t13 || $[18] !== t9) {
    t14 = <div className={t9}>{t10}{t13}</div>;
    $[16] = t10;
    $[17] = t13;
    $[18] = t9;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  return <div>{t7}<div className={twoCol}><div className={column}>{t8}<div className={columnBody}>{inactive.length === 0 ? <div className={emptyState}>—</div> : inactive.map(item_1 => <button key={item_1} type="button" className={inactiveChip} onClick={() => add(item_1)}>{item_1}</button>)}</div></div>{t14}</div></div>;
}
function _temp(x) {
  return x !== DIVIDER_ID;
}
interface SortableRowProps {
  id: string;
  rank: number;
  fallback: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}
function SortableRow(t0) {
  const $ = _c(36);
  const {
    id,
    rank,
    fallback,
    canMoveUp,
    canMoveDown,
    onUp,
    onDown,
    onRemove
  } = t0;
  let t1;
  if ($[0] !== id) {
    t1 = {
      id
    };
    $[0] = id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t1);
  let t2;
  if ($[2] !== transform) {
    t2 = CSS.Transform.toString(transform);
    $[2] = transform;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isDragging ? 0.5 : 1;
  let t4;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== transition) {
    t4 = {
      transform: t2,
      transition,
      opacity: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = transition;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const style = t4;
  const t5 = fallback ? fallbackRow : activeRow;
  let t6;
  if ($[8] !== attributes || $[9] !== listeners) {
    t6 = <span className={dragHandle} {...attributes} {...listeners} aria-label="Ziehen zum Sortieren">⋮⋮</span>;
    $[8] = attributes;
    $[9] = listeners;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = fallback ? rankPillFallback : rankPill;
  let t8;
  if ($[11] !== rank || $[12] !== t7) {
    t8 = <span className={t7}>{rank}</span>;
    $[11] = rank;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== id) {
    t9 = <span className={rowLabel}>{id}</span>;
    $[14] = id;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = !canMoveUp;
  let t11;
  if ($[16] !== onUp || $[17] !== t10) {
    t11 = <button type="button" disabled={t10} onClick={onUp} className={iconBtn} aria-label="Hoch">↑</button>;
    $[16] = onUp;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = !canMoveDown;
  let t13;
  if ($[19] !== onDown || $[20] !== t12) {
    t13 = <button type="button" disabled={t12} onClick={onDown} className={iconBtn} aria-label="Runter">↓</button>;
    $[19] = onDown;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== onRemove) {
    t14 = <button type="button" onClick={onRemove} className={iconBtn} aria-label="Entfernen">×</button>;
    $[22] = onRemove;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t11 || $[25] !== t13 || $[26] !== t14) {
    t15 = <div className={rowActions}>{t11}{t13}{t14}</div>;
    $[24] = t11;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== setNodeRef || $[29] !== style || $[30] !== t15 || $[31] !== t5 || $[32] !== t6 || $[33] !== t8 || $[34] !== t9) {
    t16 = <div ref={setNodeRef} style={style} className={t5}>{t6}{t8}{t9}{t15}</div>;
    $[28] = setNodeRef;
    $[29] = style;
    $[30] = t15;
    $[31] = t5;
    $[32] = t6;
    $[33] = t8;
    $[34] = t9;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
}
function DividerSortable(t0) {
  const $ = _c(16);
  const {
    label
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      id: DIVIDER_ID
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t1);
  let t2;
  if ($[1] !== transform) {
    t2 = CSS.Transform.toString(transform);
    $[1] = transform;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isDragging ? 0.5 : 1;
  let t4;
  if ($[3] !== t2 || $[4] !== t3 || $[5] !== transition) {
    t4 = {
      transform: t2,
      transition,
      opacity: t3
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = transition;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const style = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className={dragHandle} aria-hidden={true}>⋮⋮</span>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== label) {
    t6 = <span className={dividerLabel}>— {label} —</span>;
    $[8] = label;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== attributes || $[11] !== listeners || $[12] !== setNodeRef || $[13] !== style || $[14] !== t6) {
    t7 = <div ref={setNodeRef} style={style} className={dividerRow} {...attributes} {...listeners} aria-label="Fallback-Trenner ziehen">{t5}{t6}</div>;
    $[10] = attributes;
    $[11] = listeners;
    $[12] = setNodeRef;
    $[13] = style;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}